import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { usePostCartJsActionMutation } from "src/features/checkout/cartJsApi"
import { toast } from "react-toastify"
import { setCart } from "src/features/checkout/checkoutSlice"
import { useCartLinesAddMutation } from "src/features/checkout/storefrontCartApi"

const genericErrorMessage =
  "Something went wrong while trying to add the product to your cart. Please try again in a while."

const getProductVariant = (product) => {
  const id = product.variants.edges[0]?.node?.id
  const legacyId = id.replace("gid://shopify/ProductVariant/", "")
  return { id, legacyId }
}

const useAddToCart = () => {
  const dispatch = useDispatch()
  const cart = useSelector((state) => state.checkout.cart)
  const [productVariant, setProductVariant] = useState(null)
  const [quantity, setQuantity] = useState(1)

  const [
    postCartAction,
    {
      data: postCartActionData,
      isLoading: postCartActionLoading,
      error: postCartActionError,
    },
  ] = usePostCartJsActionMutation()

  const [
    cartLinesAdd,
    {
      data: cartLinesAddData,
      isLoading: cartLinesAddLoading,
      error: cartLinesAddError
    }
  ] = useCartLinesAddMutation()

  const addToCart = (product, quantity = 1) => {
    const variant = getProductVariant(product)

    if (variant.id) {
      setQuantity(quantity)
      setProductVariant(variant)
    }
  }

  useEffect(() => {
    if (!productVariant?.id) return

    // Add the product to cart using Shopify AJAX API.
    postCartAction({
      action: "add",
      params: {
        id: productVariant.legacyId,
        quantity,
      },
    })
  }, [productVariant])

  useEffect(() => {
    if (postCartActionData) {
      // Add the item to checkout after successfully adding the product to cart using Shopify AJAX API.
      cartLinesAdd({
        cartId: cart.id,
        lines: [
          {
            merchandiseId: productVariant.id,
            quantity,
          },
        ],
      })
    }
  }, [postCartActionData])

  useEffect(() => {
    if (!cartLinesAddData) return

    if (cartLinesAddData?.cartLinesAdd?.cart) {
      dispatch(setCart(cartLinesAddData.cartLinesAdd.cart))
    }

    // Check for graphql warnings.
    const warning =
      cartLinesAddData.cartLinesAdd?.warnings?.[0] ?? null

    if (warning) {
      toast.warn(`${warning.message}`)
      return
    }

    // The product was successfully added to the Cart and Checkout if we reached this point.
    toast.success("Added to Cart")
  }, [cartLinesAddData])

  useEffect(() => {
    if (!postCartActionError) return

    let errorMessage = postCartActionError?.data?.message

    if (errorMessage) {
      const errorDescription = postCartActionError?.data?.description

      if (errorDescription) {
        errorMessage += `: ${errorDescription}`
      }

      toast.error(`${errorMessage}`)
    } else {
      toast.error(genericErrorMessage)
    }
  }, [postCartActionError])

  useEffect(() => {
    if (!cartLinesAddError) return

    if (cartLinesAddError?.data?.message) {
      toast.error(cartLinesAddError.data.message)
    } else {
      toast.error(genericErrorMessage)
    }
  }, [cartLinesAddError])

  return [
    addToCart,
    {
      isLoading: cartLinesAddLoading || postCartActionLoading,
    },
  ]
}

export default useAddToCart
